import { memo, useCallback, useRef, useEffect, useMemo, useId } from 'react';
import type { CSSProperties } from 'react';
import { useGameStore, isHotseatMode } from '../../store/game-store';
import { usePreferencesStore } from '../../store/preferences-store';
import { squareToSVGCoords, squareToPosition, isPlayableSquare, svgCoordsToSquare } from '../../utils/board-coordinates';
import type { Move, BoardState, BoardNotation, Square } from '@draughts/shared';
import { Color, GameStatus, GameVariant, VARIANT_CONFIGS, CAPTURE_STEP_MS } from '@draughts/shared';
import { getAllLegalMoves, getBlockedKingState } from '@draughts/core';
import Piece from './Piece';
import DraggedPiece from './DraggedPiece';
import BoardSlab from './BoardSlab';
import { glassIncidence } from './board-materials';
import BoardCoords from './BoardCoords';
import { usePieceColorPair, useBoardColors } from './use-appearance';
import { boardColorHex } from './board-themes';
import { resolveMoveHintColor, DOT_CORE_FRACTION } from './board-highlight-colors';
import { blockedKingDotInk, BK_DOT_R, BK_DOT_STEP, BK_DOT_CY } from './blocked-king-dots';
import { PieceLodContext, useBoardLod } from './piece-lod';
import { log } from '../../utils/logger';
import { resolveMoveIntent, resolveCaptureStep, captureTargetSquares, premoveCaptureLandings, findMatchingMove, resolveDropSquare, resolveCaptureDropSquare } from '../../utils/move-input';
import { setMoveSource } from '../../utils/audio';
import './GameBoard.css';

const SQUARE_SIZE = 90; // pixels per square
// Snap tolerance for an imprecise drag-drop: release within one square of a legal
// destination's centre and the move still lands (forgiving drop). See resolveDropSquare.
const SNAP_RADIUS = SQUARE_SIZE;

interface GameBoardProps {
  /**
   * La damiera e' inerte: nessuna selezione, nessuna pre-mossa a schermo.
   *
   * Diverso da `!isMyTurn`, che e' il turno dell'avversario — li' la pre-mossa
   * e' viva ed e' giusto poter toccare. Qui la partita non e' giocabile
   * affatto: finita, oppure con la connessione caduta. La selezione rimasta a
   * schermo va tolta, o continua a mostrare i pallini di una mossa che non si
   * puo' piu' fare — che e' esattamente cosa vuol dire «mi faceva ancora
   * muovere».
   */
  inerte?: boolean;
  isOnlineMode?: boolean;
  onOnlineMove?: (move: Move) => void;
  isMyTurn?: boolean;
  /** Allow queuing a pre-move during the opponent's turn (auto-played when it
   *  becomes our turn, if still legal). */
  allowPremove?: boolean;
  /** Flip the board 180° (for black player to see their pieces at bottom) */
  flipped?: boolean;
  /** Scavalca la preferenza di grafica `boardNotation` (solo l'analisi dell'editor). */
  notation?: BoardNotation;
  /** Puzzle hint: highlight this square (the piece to move). Only the from-square
   *  is revealed — never the destination. */
  hintSquare?: number | null;
  /** Online: this player's colour (for the takeback pickup turn check). */
  onlinePlayerColor?: 'white' | 'black';
  /** Online: true only for casual games where takeback is permitted. */
  takebackAllowed?: boolean;
  /** Online casual: request a takeback rolling back to `toPly` (pickup during review). */
  onTakebackRequest?: (toPly: number) => void;
  /** Online rated: a takeback pickup was attempted where it's disabled. */
  onRatedTakebackBlocked?: () => void;
  /**
   * Richiamo dei pezzi muovibili dopo un tocco a vuoto. Spento nei puzzle: in una posizione
   * con presa obbligatoria l'insieme dei pezzi che possono muovere È la casella di partenza
   * della soluzione, che `hintSquare` concede solo a richiesta e a un costo.
   */
  movableHintEnabled?: boolean;
  /** Editor analysis board: a hot-seat scratch line where BOTH colours are movable and
   *  picking a piece up during review truncates the continuation instead of being
   *  refused. Never set this in a real game — it relaxes the pvp takeback guard. */
  analysisMode?: boolean;
  /**
   * ALLA CIECA: i pezzi si disegnano trasparenti e ogni aiuto che ne rivelerebbe la
   * casella si spegne. Non cambia NIENTE del gioco — le regole, le mosse legali e i
   * tocchi restano quelli di sempre, perché l'input si risolve per coordinate e non
   * per bersaglio: si può giocare a pezzi invisibili senza toccare la logica.
   *
   * Lo decide `GameScreen`, che sa se chi guarda sta giocando e se la partita è finita.
   * Default `false` per le scacchiere montate senza passare di lì (puzzle, analisi).
   */
  blind?: boolean;
  /**
   * Sbirciata in corso: la scacchiera si rivela per qualche istante. Prop separato da
   * `blind`, e non un `blind={false}` temporaneo, perché la classe `blind` deve restare
   * sull'<svg> anche mentre si sbircia: è quella che porta la transizione, e togliendola
   * la rivelazione scatterebbe di colpo invece di dissolversi.
   */
  peeking?: boolean;
  /**
   * ANNUNCIO DELL'ULTIMA MOSSA: riaccende per qualche secondo l'alone su partenza, arrivo
   * e caselle catturate. Bendati è il solo canale che dice cosa ha giocato l'avversario —
   * nel gioco alla cieca vero la mossa viene detta a voce, qui la dicono queste caselle.
   * Lo pilota `useBlindMoveAnnouncement` tramite `GameScreen`.
   */
  revealLastMove?: boolean;
}

function GameBoard({ isOnlineMode = false, onOnlineMove, isMyTurn = true, inerte = false, allowPremove = false, flipped = false, notation, hintSquare = null, onlinePlayerColor, takebackAllowed = false, onTakebackRequest, onRatedTakebackBlocked, analysisMode = false, movableHintEnabled = true, blind = false, peeking = false, revealLastMove = false }: GameBoardProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  // True while a multi-capture is being played out locally in online mode, so we
  // can forward the completed move to the server once the sequence finishes.
  // True while a capture chosen by its final square is auto-animating: block input.
  const autoPlayingRef = useRef(false);
  // Pending timers for the auto-play animation (cleared on unmount).
  const autoPlayTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  /**
   * Il `<g>` che porta la pedina trascinata. Si sposta scrivendogli un `transform`, senza
   * passare da React: vedi `handlePointerMove` e la nota in `DraggedPiece`.
   */
  const dragLayerRef = useRef<SVGSVGElement | null>(null);
  /**
   * Il rettangolo dell'`<svg>`, misurato UNA VOLTA per presa.
   *
   * `clientToSVG` faceva `getBoundingClientRect()` a ogni `pointermove`, e su un telefono
   * il touch ne emette fino a centoventi al secondo: centoventi letture di layout
   * sincrone al secondo per un rettangolo che durante una presa non cambia — l'`<svg>` ha
   * `touch-action: none`, quindi la pagina sotto il dito non scorre.
   */
  const boardRectRef = useRef<DOMRect | null>(null);
  const livePieces = useGameStore((state) => state.pieces);
  const variant = useGameStore((state) => state.variant);
  const reviewPieces = useGameStore((state) => state.reviewPieces);
  const reviewIndex = useGameStore((state) => state.reviewIndex);
  const reviewTurn = useGameStore((state) => state.reviewTurn);
  const reviewKingMoveStreak = useGameStore((state) => state.reviewKingMoveStreak);
  const isReviewing = reviewIndex !== null;
  const moveHistory = useGameStore((state) => state.moveHistory);
  const pieces = isReviewing && reviewPieces ? reviewPieces : livePieces;
  const captureReplay = useGameStore((state) => state.captureReplay);
  const cancelCaptureReplay = useGameStore((state) => state.cancelCaptureReplay);
  /**
   * Il replay della presa altrui, se c'e' ed e' il caso di mostrarlo.
   *
   * Alla cieca si spegne sul PROP `blind`, non su `blindActive`: durante una sbirciata
   * la damiera torna normale, e il fantasma ricomparirebbe a meta' strada su una
   * posizione congelata. Nella revisione vince `reviewPieces`, che sta guardando altro.
   */
  const replay = captureReplay && !blind && !isReviewing ? captureReplay : null;
  // I pezzi da DISEGNARE. `pieces` resta la damiera viva perche' e' quella che leggono
  // gli handler dell'input; qui sotto scorre il fotogramma di prima.
  const renderPieces = replay ? replay.pieces : pieces;

  // Last move to highlight (from/to/capture squares)
  // In review mode: the move that led to the position shown
  // In live mode: the most recent move played
  const lastMove = useMemo(() => {
    if (isReviewing) {
      // reviewIndex is the position (0 = initial, N = after Nth move)
      // The move that produced this position is moveHistory[reviewIndex - 1]
      if (reviewIndex !== null && reviewIndex > 0) {
        return moveHistory[reviewIndex - 1] ?? null;
      }
      return null;
    }
    return moveHistory.length > 0 ? moveHistory[moveHistory.length - 1] : null;
  }, [moveHistory, reviewIndex, isReviewing]);

  // Compute squares to highlight for the last move
  //
  // Spento mentre scorre il replay di una presa altrui, e non e' un dettaglio: `lastMove`
  // viene dalla `moveHistory` VIVA, gia' scritta, quindi la casella d'arrivo e tutte le
  // catturate si accenderebbero PRIMA che il pezzo ci arrivi — mentre nella presa giocata
  // a mano l'alone compare solo alla fine. Sarebbe la stessa asimmetria da un'altra porta.
  const lastMoveSquares = useMemo(() => {
    if (!lastMove || replay) return { from: null as number | null, to: null as number | null, captures: [] as number[] };
    return {
      from: lastMove.from,
      to: lastMove.to,
      captures: lastMove.captures.map((c) => c.square),
    };
  }, [lastMove, replay]);
  // ALLA CIECA gli aiuti si spengono TUTTI, e non basta nascondere i pezzi: i pallini
  // delle mosse legali disegnano dove può andare il pezzo che hai toccato, l'alone
  // dell'ultima mossa ne segna la casella d'arrivo. Lasciarli accesi rivelerebbe la
  // posizione meglio dei pezzi stessi. Durante una sbirciata la scacchiera torna
  // normale, aiuti compresi.
  const blindActive = blind && !peeking;
  // Le preferenze si LEGGONO SEMPRE e si combinano dopo. Metterle a destra di un `||`
  // salterebbe la chiamata quando l'annuncio è acceso, ed è una chiamata condizionata a
  // un hook — React non lo perdona.
  const prefMoveHints = usePreferencesStore((state) => state.showMoveHints);
  const prefLastMove = usePreferencesStore((state) => state.showLastMove);
  const prefLastMoveCaptures = usePreferencesStore((state) => state.showLastMoveCaptures);
  // L'annuncio SCAVALCA la preferenza dell'utente, e non per svista: fuori dalla cieca
  // «evidenzia ultima mossa» è una comodità, qui è l'unica cosa che dice cosa ha giocato
  // l'altro — con la preferenza spenta la modalità sarebbe ingiocabile.
  const announcing = blindActive && revealLastMove;
  const showMoveHints = prefMoveHints && !blindActive;
  const showLastMove = announcing || (prefLastMove && !blindActive);
  const showLastMoveCaptures = announcing || (prefLastMoveCaptures && !blindActive);
  const preferredNotation = usePreferencesStore((state) => state.boardNotation);
  // La preferenza è la regola; il prop serve all'unico posto che può scavalcarla,
  // l'analisi aperta DALL'editor, dove la notazione è quella scelta lì col pulsante.
  const boardNotation = notation ?? preferredNotation;
  const premoveEnabled = usePreferencesStore((state) => state.premoveEnabled);
  const boardMaterial = usePreferencesStore((state) => state.boardMaterial);
  // Serve all'inchiostro dei pallini della dama bloccata: la tinta sotto il pallino e'
  // la parete del pezzo sugli stili in rilievo e la faccia su quelli piatti.
  const pieceStyle = usePreferencesStore((state) => state.pieceStyle);
  const currentTurn = useGameStore((state) => state.currentTurn);
  const status = useGameStore((state) => state.status);
  /*
   * Lo stato delle REGOLE, che una damiera ricostruita a mano deve portarsi dietro.
   *
   * `boardFromStore` nello store lo dice a lettere chiare: ogni damiera rimessa insieme
   * per generare mosse legali deve avere `ruleMode` (breakthrough/antidama) e lo stato
   * frisone (`kingMoveStreak` + `frisianEndgamePly`), o la regola del re bloccato e il
   * pari di 2 dame contro 1 smettono di valere IN SILENZIO. L'effetto della premossa qui
   * sotto era l'unico posto dell'app che ricostruiva una damiera senza di loro — e online
   * sono proprio i campi che `syncFromExternal` si tiene stretti dal server per non
   * offrire mosse che il server poi rifiuta.
   */
  const ruleMode = useGameStore((state) => state.ruleMode);
  const halfMoveClock = useGameStore((state) => state.halfMoveClock);
  const positionHistory = useGameStore((state) => state.positionHistory);
  const kingMoveStreak = useGameStore((state) => state.kingMoveStreak);
  const frisianEndgamePly = useGameStore((state) => state.frisianEndgamePly);
  const premove = useGameStore((state) => state.premove);
  const premoveStartDrag = useGameStore((state) => state.premoveStartDrag);
  const setPremove = useGameStore((state) => state.setPremove);
  const clearPremove = useGameStore((state) => state.clearPremove);
  const selectedSquare = useGameStore((state) => state.selectedSquare);
  const legalMoves = useGameStore((state) => state.legalMoves);
  const dragState = useGameStore((state) => state.dragState);
  // NOTE: dragPosition is intentionally NOT subscribed here — it changes on every
  // pointermove. The floating piece is rendered by <DraggedPiece>, the sole
  // subscriber, so a drag re-renders just that piece and not the whole board.
  const captureInProgress = useGameStore((state) => state.captureInProgress);
  const selectSquare = useGameStore((state) => state.selectSquare);

  /*
   * Damiera inerte: via la selezione e la pre-mossa rimaste a schermo.
   *
   * Senza questa pulizia il pezzo scelto un istante prima della caduta di rete
   * resta illuminato, coi suoi pallini di destinazione, su una partita in cui
   * nessun tocco fa piu' niente: la damiera continua a promettere una mossa
   * che non puo' avvenire. Il gesto e' gia' bloccato in `handlePointerDown` —
   * qui si toglie la promessa, non il pericolo.
   */
  useEffect(() => {
    if (!inerte) return;
    selectSquare(null);
    clearPremove();
  }, [inerte, selectSquare, clearPremove]);
  const startDrag = useGameStore((state) => state.startDrag);
  const endDrag = useGameStore((state) => state.endDrag);
  const stopDrag = useGameStore((state) => state.stopDrag);
  const cancelDrag = useGameStore((state) => state.cancelDrag);
  const makeMove = useGameStore((state) => state.makeMove);
  const gameMode = useGameStore((state) => state.gameMode);
  const localPlayerColor = useGameStore((state) => state.localPlayerColor);
  const commitTakeback = useGameStore((state) => state.commitTakeback);
  const executeStep = useGameStore((state) => state.executeStep);
  const findDirectMove = useGameStore((state) => state.findDirectMove);
  const captureSourceChoices = useGameStore((state) => state.captureSourceChoices);
  const pendingCaptureTarget = useGameStore((state) => state.pendingCaptureTarget);
  const selectCaptureSource = useGameStore((state) => state.selectCaptureSource);
  const clearCaptureChoices = useGameStore((state) => state.clearCaptureChoices);
  const movableHint = useGameStore((state) => state.movableHint);
  const flashMovableHint = useGameStore((state) => state.flashMovableHint);
  const clearMovableHint = useGameStore((state) => state.clearMovableHint);

  // Calculate board size and orientation based on variant
  const boardSize = useMemo(() => VARIANT_CONFIGS[variant].size, [variant]);
  const darkSquareBottomRight = useMemo(() => VARIANT_CONFIGS[variant].darkSquareBottomRight, [variant]);
  // All-squares variants (Turkish/Armenian/Dameo/Croda): every square is playable, row-major numbering
  const allSquares = useMemo(() => {
    const cfg = VARIANT_CONFIGS[variant];
    return cfg.playableSquares === cfg.size * cfg.size;
  }, [variant]);
  const boardPixelSize = boardSize * SQUARE_SIZE;

  // Resolve board/piece colors: 'auto' (default) → per-variant colors from the
  // MasterMatrix; any explicit theme overrides the per-variant default.
  const boardColors = useBoardColors(variant);
  const pieceColorPair = usePieceColorPair(variant);

  // Il verde dei segni di destinazione, ricavato dalla casella su cui cadono (vedi
  // `board-highlight-colors.ts`). Due valori e non uno: sulle varianti a caselle tutte
  // giocabili pallini e anelli finiscono anche sulle chiare, e una casella chiara e una
  // scura possono legittimamente chiedere due verdi diversi.
  const hintColors = useMemo(
    () => ({
      dark: resolveMoveHintColor(boardColorHex(boardColors.dark)),
      light: resolveMoveHintColor(boardColorHex(boardColors.light)),
    }),
    [boardColors.dark, boardColors.light],
  );
  // `useId` per lo stesso motivo di BoardSlab e Piece: gli id dei `<defs>` sono globali nel
  // documento, e due scacchiere sulla stessa pagina (partita + anteprima) si ruberebbero il
  // gradiente a vicenda. I due punti sono ripuliti perche' non sono validi in un selettore.
  const uid = useId().replace(/[^\w-]/g, '');
  const hintGradientId = useCallback((dark: boolean) => `hint-fade-${dark ? 'd' : 'l'}-${uid}`, [uid]);

  const isHintSquareDark = useCallback(
    (square: number): boolean => {
      const { row, col } = squareToPosition(square, boardSize, darkSquareBottomRight, allSquares);
      return isPlayableSquare(row, col, boardSize, darkSquareBottomRight);
    },
    [boardSize, darkSquareBottomRight, allSquares],
  );
  const hintColorFor = useCallback(
    (square: number): string => (isHintSquareDark(square) ? hintColors.dark : hintColors.light),
    [hintColors, isHintSquareDark],
  );

  // Livello di dettaglio del rilievo 3D, dal diametro REALE del pezzo: una 14x14 a
  // 360px dà pezzi da 18px, dove tre solchi concentrici sarebbero fango.
  const pieceLod = useBoardLod(svgRef, boardPixelSize, SQUARE_SIZE * 0.8);

  /*
   * Spedisce la mossa online appena la presa è DAVVERO finita.
   *
   * Prima la fine presa si deduceva da `captureInProgress` che passava a `null`,
   * e da lì si spediva l'ultima mossa dello storico. Due difetti in uno:
   * `syncFromExternal` azzera quel campo a ogni aggiornamento arrivato dal
   * server, quindi un `game:update` che atterrava a metà di una presa faceva
   * concludere «finita» — e l'ultima mossa dello storico, in quell'istante,
   * poteva essere quella dell'AVVERSARIO, che veniva rispedita come propria.
   *
   * Ora la conclusione è un fatto, non una deduzione: `executeStep` e `endDrag`
   * restituiscono la mossa completata (e `null` se il passo non era l'ultimo), e
   * si spedisce QUELLA. Nessuno stato intermedio da interpretare, nessuna
   * finestra in cui un aggiornamento esterno possa mentire.
   */
  const inviaSeCompletata = useCallback((mossa: Move | null) => {
    if (!mossa || !isOnlineMode || !onOnlineMove) return;
    log.info('GameBoard', 'Multi-capture complete, sending move', { from: mossa.from, to: mossa.to });
    onOnlineMove(mossa);
  }, [isOnlineMode, onOnlineMove]);

  /**
   * Chiude la presa: riporta a zero lo spostamento del layer e butta il rettangolo.
   *
   * Si chiama con qualunque esito — rilascio, annullamento, smontaggio — perché un
   * `transform` residuo farebbe ricomparire la pedina spostata alla presa successiva.
   */
  const resetDragLayer = useCallback(() => {
    if (dragLayerRef.current) dragLayerRef.current.style.transform = '';
    boardRectRef.current = null;
  }, []);

  // Sullo smontaggio si spegne tutto quello che questa damiera ha acceso ALTROVE.
  // Anche il richiamo dei pezzi muovibili: senza, chi tocca a vuoto e lascia subito la
  // schermata se lo ritrova acceso al rientro.
  useEffect(() => () => {
    autoPlayTimersRef.current.forEach((t) => clearTimeout(t));
    autoPlayTimersRef.current = [];
    clearMovableHint();
    // I timer del replay vivono nello store, non qui: senza questo continuerebbero a
    // battere su una damiera che nessuno guarda piu'.
    cancelCaptureReplay();
    /*
     * E LA PRESA, per la stessa ragione: `dragState` vive nello store e allo smontaggio
     * gli sopravvive. Il `pointercancel` del browser arriva a un `<svg>` ormai staccato
     * e non risale piu' al listener di React, quindi nessuno la scioglierebbe: resta
     * ORFANA, con un `pointerId` che non tornera' mai piu'. Su touch quel `pointerId`
     * non combacia con nessun dito nuovo, e le guardie di `handlePointerDown`/`Move`/
     * `Up`/`Cancel` respingono tutto — cioe' la damiera della PROSSIMA partita nasce
     * morta, con l'orologio che intanto corre.
     */
    resetDragLayer();
    cancelDrag();
  }, [clearMovableHint, cancelCaptureReplay, resetDragLayer, cancelDrag]);

  // Convert client coordinates to SVG coordinates
  const clientToSVG = useCallback((clientX: number, clientY: number) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };

    const rect = boardRectRef.current ?? svg.getBoundingClientRect();
    const scaleX = boardPixelSize / rect.width;
    const scaleY = boardPixelSize / rect.height;

    let x = (clientX - rect.left) * scaleX;
    let y = (clientY - rect.top) * scaleY;

    // When board is flipped, invert the coordinates
    if (flipped) {
      x = boardPixelSize - x;
      y = boardPixelSize - y;
    }

    return { x, y };
  }, [boardPixelSize, flipped]);

  // Drive the step-by-step capture machinery over a list of landing squares on a timer,
  // animating each hop. Used both to play a whole capture from the start and to complete
  // the remaining hops when a player clicks the final ring mid-sequence.
  const autoPlaySteps = useCallback((remaining: number[]) => {
    if (remaining.length === 0) return;
    autoPlayingRef.current = true;
    let i = 0;
    const playNext = () => {
      // L'esito di OGNI passo, non solo dell'ultimo: `executeStep` sa da sé quale
      // era l'ultimo, e chiederlo qui vorrebbe dire rifare quel conto — cioè
      // tornare a dedurre.
      const completata = executeStep(remaining[i]!);
      inviaSeCompletata(completata);
      i += 1;
      if (i < remaining.length) {
        autoPlayTimersRef.current.push(setTimeout(playNext, CAPTURE_STEP_MS));
      } else {
        autoPlayingRef.current = false;
      }
    };
    autoPlayTimersRef.current.push(setTimeout(playNext, CAPTURE_STEP_MS));
  }, [executeStep, inviaSeCompletata]);

  // Play a whole multi-capture chosen by its final square, animating each hop by
  // driving the existing step-by-step machinery on a timer.
  const autoPlayCapture = useCallback((move: Move) => {
    const path = move.path;
    if (!path || path.length <= 1) {
      // Presa di un solo salto: `makeMove` la applica tutta, non c'è nessuna
      // sequenza da seguire — quindi è già completa, e si spedisce subito.
      makeMove(move);
      inviaSeCompletata(move);
      return;
    }
    makeMove(move); // starts the sequence at the first hop (path[0])
    autoPlaySteps(path.slice(1)); // landing squares still to play, incl. final
  }, [makeMove, autoPlaySteps, inviaSeCompletata]);

  // Map a clicked/dropped square to a move and perform it. Returns false when the
  // square is not a valid destination (caller should deselect).
  const executeIntent = useCallback((square: number, moves: Move[] = legalMoves): boolean => {
    const intent = resolveMoveIntent(moves, square);
    log.info('GameBoard', 'executeIntent', { square, kind: intent.kind, movesConsidered: moves.length });
    switch (intent.kind) {
      case 'simple':
        if (isOnlineMode && onOnlineMove) onOnlineMove(intent.move);
        else makeMove(intent.move);
        return true;
      case 'capture-direct':
        // Niente bandierina «presa online in corso»: la spedizione ora segue il
        // valore di ritorno di chi CONCLUDE la presa (vedi `inviaSeCompletata`).
        autoPlayCapture(intent.move);
        return true;
      case 'capture-sequential':
        makeMove(intent.startMove); // starts step-by-step; player picks the rest
        return true;
      case 'none':
        log.warn('GameBoard', 'executeIntent: no legal move for square, deselecting', { square });
        return false;
    }
  }, [legalMoves, isOnlineMode, onOnlineMove, makeMove, autoPlayCapture]);

  /*
   * ACCODA / ANNULLA UNA PRE-MOSSA DURANTE IL TURNO AVVERSARIO.
   *
   * Tre regole, e l'ordine dei rami serve a non farle litigare:
   *
   * 1. Si sceglie SEMPRE prima il pezzo, poi dove va: la pre-mossa «arriva qui» a una
   *    casella sola non esiste. Finche' non c'e' un pezzo scelto, l'unica cosa che si puo'
   *    toccare e' una nostra pedina.
   * 2. Col pezzo scelto, la destinazione e' LIBERA: qualunque casella toccata e' l'arrivo,
   *    anche senza pallino, anche se ci sta una NOSTRA pedina. La pre-mossa e' un intento —
   *    l'arrivo lontano di una presa multipla che ancora non esiste e' legittimo, e cosi' la
   *    ricattura anticipata sopra una propria pedina (l'avversario la mangia, e al cambio di
   *    turno ci si arriva); la mossa vera la risolve `findMatchingMove`.
   *    Per questo il ramo della destinazione viene PRIMA di quello del pezzo: una regola
   *    senza eccezioni di raggiungibilita'. Il prezzo, scelto in cambio: per cambiare pezzo
   *    si annulla prima (un tocco su casella chiara) e poi si tocca il pezzo nuovo.
   * 3. Una pre-mossa in coda muore al tocco successivo, qualunque esso sia. Prima era
   *    imprevedibile: certi tocchi la cancellavano, altri la sostituivano di nascosto con
   *    una pre-mossa «arriva qui», altri la lasciavano viva.
   *
   * Due eccezioni alla regola 3, e non sono qui: mentre scorre il replay della presa
   * avversaria il tocco e' assorbito per chiudere il replay, e mentre una presa si
   * auto-anima e' ignorato. In entrambi i casi `handlePointerDown` esce prima di
   * chiamarci, quindi la pre-mossa sopravvive - ed e' giusto: quei tocchi non sono
   * scelte sulla damiera vera.
   */
  const handlePremovePointerDown = useCallback((event: React.PointerEvent<SVGSVGElement>) => {
    const { x, y } = clientToSVG(event.clientX, event.clientY);
    const square = svgCoordsToSquare(x, y, SQUARE_SIZE, boardSize, darkSquareBottomRight, allSquares);
    const premoveColor = currentTurn === Color.WHITE ? Color.BLACK : Color.WHITE;
    const isOwnPiece = square !== null && pieces.some((p) => p.square === square && p.color === premoveColor);

    /*
     * UN SECONDO DITO NON DISTURBA LA PEDINA CHE IL PRIMO HA IN MANO.
     *
     * Con una presa viva l'unico puntatore che arriva fin qui e' quello che la tiene (gli
     * altri li respinge `handlePointerDown`, e una presa orfana la scioglie), e il suo
     * gesto si chiude col `pointerup`. Quindi qualunque `pointerdown` con `dragState`
     * valorizzato e' rumore: se lo si lasciasse passare, il ramo qui sotto prenderebbe in
     * mano un'ALTRA pedina e riscriverebbe `dragState`, lasciando il primo dito a
     * trascinare una pedina che non e' piu' la sua. Prima il guardiano c'era solo su due
     * rami su otto; ora vale per tutti.
     */
    if (dragState !== null) return;

    /*
     * Regola 3: una pre-mossa in coda muore al tocco successivo, e il tocco si CONSUMA —
     * tranne quando prende in mano una nostra pedina, che apre subito la composizione
     * della pre-mossa seguente. Vale anche per un tocco fuori griglia (`square` nullo:
     * li' `isOwnPiece` e' falso e il ramo si limita a buttarla).
     *
     * Con una pre-mossa in coda `selectedSquare` e' per forza nullo — lo azzera
     * `setPremove` — quindi questo ramo e quello della composizione non si sovrappongono.
     */
    if (premove !== null) {
      log.info('GameBoard', 'Pre-mossa annullata dal tocco successivo', { to: premove.to, square });
      clearPremove();
      if (isOwnPiece) {
        event.currentTarget.setPointerCapture(event.pointerId);
        premoveStartDrag(square, x, y, event.pointerId);
      }
      return;
    }

    // Casella chiara o fuori griglia: «toccare fuori dal gioco» lascia il pezzo scelto.
    // E' anche la strada per CAMBIARE pezzo, ora che toccarne un altro accoda l'arrivo.
    if (square === null) {
      if (selectedSquare !== null) selectSquare(null);
      return;
    }

    // Regola 2: col pezzo scelto, qualunque altra casella e' l'arrivo — nostre pedine
    // comprese (la ricattura anticipata).
    if (selectedSquare !== null) {
      // Ritoccare il pezzo GIA' scelto non accoda una pre-mossa verso se' stesso: riattacca
      // la presa, cosi' quel pezzo si puo' ancora trascinare invece che toccare due volte.
      if (square === selectedSquare) {
        event.currentTarget.setPointerCapture(event.pointerId);
        premoveStartDrag(square, x, y, event.pointerId);
        return;
      }
      log.info('GameBoard', 'Pre-mossa accodata', { from: selectedSquare, to: square });
      setPremove({ from: selectedSquare, to: square });
      return;
    }

    // Regola 1: niente scelto, quindi l'unica cosa che si puo' prendere e' una nostra pedina.
    if (isOwnPiece) {
      event.currentTarget.setPointerCapture(event.pointerId);
      premoveStartDrag(square, x, y, event.pointerId);
      return;
    }

    /*
     * Niente scelto e niente in coda. Qui prima un tocco su casella vuota accodava una
     * pre-mossa «arriva qui» a una casella sola: tolta.
     *
     * E non si illumina niente, apposta. Il richiamo sui pezzi muovibili e' un istituto del
     * PROPRIO turno — dice quali pedine si possono muovere ORA — e qui muove l'avversario:
     * accenderlo direbbe una cosa che non c'entra, e per giunta falsa, visto che per una
     * pre-mossa si puo' scegliere qualunque propria pedina, muovibile o no.
     */
    log.info('GameBoard', 'Pre-mossa: tocco a vuoto, nessun pezzo scelto', { square });
  }, [clientToSVG, boardSize, darkSquareBottomRight, allSquares, currentTurn, pieces, selectedSquare, selectSquare, dragState, premove, premoveStartDrag, setPremove, clearPremove]);

  const handlePointerDown = useCallback((event: React.PointerEvent<SVGSVGElement>) => {
    // Stamp the gesture for the move sound: a tap commits from pointer-down.
    // Picking up a piece produces no sound, and a drag re-stamps 'drag' on
    // pointer-up, so setting it unconditionally here is safe.
    setMoveSource('tap');
    // Il rettangolo dell'<svg> per tutto il gesto: da qui in poi `clientToSVG` non tocca
    // piu' il layout. Lo si butta alla fine della presa (`resetDragLayer`).
    boardRectRef.current = event.currentTarget.getBoundingClientRect();
    /*
     * UN SECONDO DITO NON DIROTTA LA PRESA IN CORSO — MA UNA PRESA ORFANA SI SCIOGLIE.
     *
     * Il ramo del pre-mossa più sotto si difendeva già («un secondo dito non deve
     * disturbare quello che ha la pedina in mano»), il ramo normale no: un `pointerdown`
     * su un'altra pedina chiamava `startDrag` e riscriveva `dragState`, lasciando il primo
     * dito a trascinare una pedina che non era più la sua.
     *
     * La sola disuguaglianza fra i `pointerId`, però, non distingue due situazioni
     * opposte: «un altro dito, mentre il primo è ancora sul vetro» e «il dito che aveva
     * la pedina non c'è più». La seconda succede quando il `pointerup` non arriva mai —
     * schermo che si spegne, app in secondo piano, smontaggio a dito giù. Su touch ogni
     * contatto nuovo prende un `pointerId` nuovo, quindi da lì in poi NIENTE combacia
     * più: `down`, `move`, `up` e `cancel` respingono tutti, e la damiera resta morta
     * per il resto della partita mentre l'orologio corre. Col mouse il `pointerId` è
     * sempre 1, combacia, e il difetto non si vede — ecco perché è arrivato in
     * produzione.
     *
     * A distinguerle è il browser: chi ha la pedina in mano ha la CATTURA del puntatore
     * sull'`<svg>` (la prendono tutte e tre le prese, qui sotto e nel ramo pre-mossa), e
     * `hasPointerCapture` risponde di sì anche quando è solo pending. Nessuna cattura =
     * presa orfana.
     *
     * `pointerId` nullo resta fuori da tutto questo: è il contratto documentato su
     * `DragState` — prese non nate da un puntatore, che accettano qualunque dito.
     */
    if (dragState !== null && dragState.pointerId !== null && event.pointerId !== dragState.pointerId) {
      if (svgRef.current?.hasPointerCapture(dragState.pointerId)) {
        log.info('GameBoard', 'pointerDown ignorato: un altro dito ha già una pedina in mano');
        return;
      }
      /*
       * Il tocco che scioglie si CONSUMA: non si prosegue nel gestore perché
       * `selectedSquare` e `legalMoves` di questa closure sono quelli della presa
       * appena morta, e proseguire potrebbe far prendere il ramo sbagliato — fino a
       * giocare una mossa che nessuno ha chiesto. Si perde un tocco, non la partita.
       */
      log.warn('GameBoard', 'presa orfana sciolta', { vecchio: dragState.pointerId, nuovo: event.pointerId });
      resetDragLayer();
      cancelDrag();
      return;
    }
    // Ignore input while a capture is auto-animating.
    if (autoPlayingRef.current) {
      log.info('GameBoard', 'pointerDown ignored (auto-playing capture)');
      return;
    }
    /*
     * IL PRIMO TOCCO CHIUDE IL REPLAY DELLA PRESA ALTRUI, E VIENE ASSORBITO.
     *
     * Assorbito e non lasciato passare: per quei due o tre decimi i pezzi disegnati sono
     * quelli di PRIMA della mossa, e far agire il tocco significherebbe agire su cio' che
     * si e' visto invece che su cio' che c'e'. Chi ha fretta tocca e la damiera e' subito
     * vera; il tocco dopo e' sulla posizione giusta.
     */
    if (captureReplay) {
      log.info('GameBoard', 'pointerDown assorbito: chiude il replay della presa avversaria');
      cancelCaptureReplay();
      return;
    }
    // Block interaction during review mode.
    // Exception: picking up your own piece in a reviewed position where it's your
    // turn triggers a takeback. In AI mode it commits locally and resumes play; in
    // online CASUAL it sends a consent request (rated → a disabled banner). The
    // reviewed side-to-move comes from the store's reviewTurn, captured from the
    // reconstructed board when review was entered (correct for any starting turn,
    // e.g. a Black-to-move puzzle continuation — not just standard White-first games).
    if (isReviewing) {
      /*
       * A partita FINITA la revisione è solo revisione.
       *
       * Il ramo qui sotto interpreta «prendi un tuo pezzo mentre guardi indietro»
       * come una richiesta di rifare la mossa, e non guardava se la partita fosse
       * ancora in corso. Su una partita conclusa succedeva questo: in casual la
       * richiesta partiva e lo store la ingoiava in silenzio (`status !==
       * 'playing'`), in valida compariva il cartello «il rifai-mossa è disattivato
       * nelle partite valide» — una spiegazione sbagliata, perché il motivo vero è
       * che la partita è finita. In entrambi i casi chi stava semplicemente
       * riguardando le mosse riceveva una risposta che non aveva chiesto.
       */
      if (status !== GameStatus.PLAYING) return;
      if (isOnlineMode) {
        const { x, y } = clientToSVG(event.clientX, event.clientY);
        const square = svgCoordsToSquare(x, y, SQUARE_SIZE, boardSize, darkSquareBottomRight, allSquares);
        if (square === null || reviewIndex === null) return;
        const piece = pieces.find((p) => p.square === square);
        const myColor = onlinePlayerColor === 'white' ? Color.WHITE : Color.BLACK;
        if (!piece || piece.color !== myColor || reviewTurn !== myColor) return;
        if (takebackAllowed) {
          log.info('GameBoard', 'Online takeback request on pickup', { square, reviewIndex });
          onTakebackRequest?.(reviewIndex);
        } else {
          log.info('GameBoard', 'Online takeback blocked (rated)', { square, reviewIndex });
          onRatedTakebackBlocked?.();
        }
        return; // never mutate the board locally in online mode
      }
      // 'pvp' = partita online/arena/torneo: la storia non si riscrive da soli.
      // In presenza (hot-seat) invece si annulla liberamente, come in analisi.
      if (gameMode === 'pvp' && !analysisMode) return;
      const { x, y } = clientToSVG(event.clientX, event.clientY);
      const square = svgCoordsToSquare(x, y, SQUARE_SIZE, boardSize, darkSquareBottomRight, allSquares);
      if (square === null) return;
      const piece = pieces.find((p) => p.square === square);
      // Reviewed-position side to move comes from the store (reviewTurn), so takeback
      // pickup works from any starting turn — including a Black-to-move continuation.
      // On the analysis board the movable side IS the reviewed side to move (hot-seat);
      // in a real game only the local player may pick up, and only on their own turn.
      // Sulla scacchiera d'analisi e in presenza il lato muovibile E' quello di
      // tratto nella posizione rivista (entrambi i colori sono in mano a chi guarda);
      // in una partita vera solo il giocatore locale, e solo al proprio turno.
      const movable = (analysisMode || isHotseatMode(gameMode)) ? reviewTurn : localPlayerColor;
      if (!piece || piece.color !== movable || reviewTurn !== movable) return;
      // Truncate history to here and go live; fall through to start the drag.
      // Skip the turn guard below: isMyTurn is still a stale `false` this render.
      log.info('GameBoard', 'Takeback on pickup in review', { square, reviewIndex });
      commitTakeback(analysisMode);
    } else if (!isMyTurn) {
      // Not our turn: allow queuing a pre-move (online / vs-AI) while the game is
      // live and the user hasn't disabled pre-moves; otherwise block interaction.
      if (allowPremove && premoveEnabled && status === GameStatus.PLAYING) {
        log.info('GameBoard', 'Not our turn, routing to pre-move path');
        handlePremovePointerDown(event);
      }
      return;
    }

    const { x, y } = clientToSVG(event.clientX, event.clientY);
    const square = svgCoordsToSquare(x, y, SQUARE_SIZE, boardSize, darkSquareBottomRight, allSquares);

    if (square !== null) {
      // Resolving an ambiguous capture: several pieces can reach the tapped landing,
      // and they're highlighted. Tap one to capture with it; tap elsewhere to cancel.
      if (captureSourceChoices.length > 0) {
        if (captureSourceChoices.includes(square) && pendingCaptureTarget !== null) {
          log.info('GameBoard', 'Ambiguous capture source chosen', { source: square, target: pendingCaptureTarget });
          const moves = selectCaptureSource(square);
          executeIntent(pendingCaptureTarget, moves);
        } else {
          clearCaptureChoices();
        }
        return;
      }

      // If there's a capture in progress
      if (captureInProgress) {
        const { possibleMoves, currentStep, currentSquare } = captureInProgress;

        // Clicking a next hop advances one jump; clicking the final ring (unique line)
        // auto-plays the remaining hops, so it's reachable at every step, not only the
        // first and last.
        const stepIntent = resolveCaptureStep(possibleMoves, currentStep, square);
        if (stepIntent.kind === 'step') {
          log.info('GameBoard', 'Capture step advance', { square, currentStep });
          inviaSeCompletata(executeStep(square));
          return;
        }
        if (stepIntent.kind === 'finish') {
          log.info('GameBoard', 'Capture finish auto-play', { square, remaining: stepIntent.remaining });
          autoPlaySteps(stepIntent.remaining);
          return;
        }

        // If clicking on the piece being moved (at currentSquare), allow drag
        if (square === currentSquare) {
          event.currentTarget.setPointerCapture(event.pointerId);
          startDrag(square, x, y, event.pointerId);
          return;
        }

        // Any other click during capture is ignored: l'unica mossa possibile è finire la
        // presa con QUEL pezzo, quindi il richiamo indica lui (è già selezionato, e il
        // richiamo gli intensifica l'alone per un attimo).
        log.warn('GameBoard', 'Tap on non-hop square mid-capture, ignored', { square, currentStep });
        flashMovableHint([currentSquare]);
        return;
      }

      // Check if there's a piece on this square
      const piece = pieces.find((p) => p.square === square);

      if (piece) {
        // Start dragging
        log.info('GameBoard', 'Piece selected', { square, color: piece.color, type: piece.type });
        event.currentTarget.setPointerCapture(event.pointerId);
        startDrag(square, x, y, event.pointerId);
      } else if (selectedSquare !== null) {
        // Clicked on a square with a piece selected: either step a capture, play a
        // whole capture by its final square, or make a simple move.
        if (!executeIntent(square)) {
          selectSquare(null);
        }
      } else {
        // Nothing selected: in a forced-capture position, a tap on a capture landing
        // plays it directly (unique source) or highlights the candidate pieces to
        // pick from (several sources — handled on the next tap above).
        const direct = findDirectMove(square);
        if (direct?.kind === 'direct') {
          executeIntent(square, direct.moves);
        } else if (!direct) {
          // Casella che non porta da nessuna parte: invece del silenzio, mostra da dove
          // si può partire.
          flashMovableHint();
        }
      }
    } else if (dragState === null) {
      // Tap on a light (non-playable) square: "tapping outside the game" cancels the current
      // input. A half-played multi-capture can't be abandoned, so it stays untouched; and a
      // piece held by another finger (multi-touch) keeps its selection so it can be dropped.
      if (captureSourceChoices.length > 0) {
        clearCaptureChoices();
      } else if (!captureInProgress && selectedSquare !== null) {
        log.info('GameBoard', 'Tap on non-playable square, deselecting');
        selectSquare(null);
      }
    }
  }, [clientToSVG, pieces, selectedSquare, dragState, resetDragLayer, cancelDrag, captureInProgress, captureReplay, cancelCaptureReplay, captureSourceChoices, pendingCaptureTarget, selectCaptureSource, clearCaptureChoices, startDrag, selectSquare, executeIntent, executeStep, autoPlaySteps, findDirectMove, flashMovableHint, isOnlineMode, isMyTurn, allowPremove, premoveEnabled, status, handlePremovePointerDown, isReviewing, reviewIndex, reviewTurn, gameMode, localPlayerColor, analysisMode, commitTakeback, boardSize, darkSquareBottomRight, allSquares]);

  // If the user disables pre-moves while one is already queued (e.g. via the
  // in-game settings modal), drop it so it doesn't auto-play when the turn flips.
  useEffect(() => {
    if (!premoveEnabled && premove) {
      log.info('GameBoard', 'Pre-move cleared (pre-moves disabled)', { to: premove.to });
      clearPremove();
    }
  }, [premoveEnabled, premove, clearPremove]);

  /*
   * Gioca la premossa in coda quando tocca di nuovo a noi — in TUTTE le modalita'.
   *
   * Qui c'e' una strada sola, ed e' voluto: la premossa e' una mossa NOSTRA, quindi deve
   * percorrere la stessa macchina di una presa cliccata a mano (`executeIntent` ->
   * `autoPlayCapture` -> un salto ogni `CAPTURE_STEP_MS`, spedita all'ultimo salto da
   * `inviaSeCompletata`). Online passava invece dal `makeMove` dello store DI RETE, che
   * applica la presa tutta insieme e spedisce subito: la presa multipla non si vedeva
   * scorrere, e l'unica animazione che restava era il replay del fantasma, nato per
   * sbaglio da `detectExternalCapture` e spento dall'eco del server dopo un giro di rete.
   *
   * Non e' solo una questione di occhio: il server accredita a chi EREDITA il turno
   * esattamente `durataAnimazionePresa(mossa)`, nel presupposto che chi muove paghi quel
   * tempo disegnando. Spedendo a t=0 la premossa non pagava niente e l'avversario veniva
   * accreditato comunque — tempo iniettato nella partita. Ora il conto torna.
   *
   * Si gira DOPO che la mossa dell'altro e' stata disegnata, e si ri-deriva la mossa sulla
   * damiera viva: il percorso e i pezzi presi vengono dalla posizione nuova (una premossa
   * porta solo l'intenzione, `from`+`to`). Se non e' piu' legale, si butta in silenzio.
   */
  useEffect(() => {
    /*
     * Il primo cancello e' «questa damiera PUO' giocare premosse», e non e' pedanteria:
     * `premove` e' stato globale, e due damiere montano `isOnlineMode` senza concederla —
     * quella dei problemi (`PuzzlePage`, dove `isOnlineMode` vale «partita in corso») e
     * quella d'analisi dell'editor. Senza questo, una premossa rimasta in coda da una
     * partita online spedirebbe la' una mossa che nessuno ha chiesto.
     */
    if (!allowPremove) return;
    if (!premove || isReviewing) return;
    if (status !== GameStatus.PLAYING) return;
    if (isOnlineMode) {
      /*
       * Online il turno si chiede a DUE fonti, e servono entrambe.
       *
       * `isMyTurn` arriva dalla damiera autorevole dello store di rete e porta dentro anche
       * «partita viva» e «connessione giocabile» (vedi `GameScreen`); `currentTurn` arriva
       * dallo store locale, che lo scrive `syncFromExternal`. Nel commit in cui atterra la
       * mossa dell'avversario le due NON concordano: gli effetti del figlio girano prima di
       * quelli del genitore, quindi `isMyTurn` e' gia' vero mentre la damiera locale e'
       * ancora al turno dell'altro. Fidandosi del solo `isMyTurn` si genererebbero le mosse
       * del colore sbagliato e la premossa verrebbe SCARTATA in silenzio; chiedendo l'accordo
       * si esce e si rigira al render dopo, quando il sync e' atterrato.
       *
       * E non si usa `localPlayerColor`: quello vale solo nelle partite locali/AI, online
       * resta al suo `WHITE` di partenza — giusto per caso col bianco, sbagliato col nero.
       */
      if (!isMyTurn || !onlinePlayerColor) return;
      const myColor = onlinePlayerColor === 'white' ? Color.WHITE : Color.BLACK;
      if (currentTurn !== myColor) return;
    } else if (currentTurn !== localPlayerColor) {
      return; // only on our turn (AI has moved)
    }
    if (autoPlayingRef.current) return; // not mid-animation
    /*
     * ...e nemmeno mentre scorre il replay della presa dell'altro.
     *
     * Senza, la premossa parte sulla damiera VIVA mentre il fantasma sta ancora
     * percorrendo il fotogramma di prima: due animazioni sovrapposte su due posizioni
     * diverse. Aspettare non costa niente nemmeno online: il server ci ha appena
     * accreditato ESATTAMENTE la durata di quell'animazione. Le due prese si vedono in
     * fila, prima la sua poi la nostra. L'effetto rigira da solo quando `captureReplay`
     * si spegne.
     */
    if (captureReplay) return;
    const board: BoardState = {
      pieces,
      currentTurn,
      status,
      variant,
      // Lo stato delle regole viaggia con la damiera: vedi la nota sulle sottoscrizioni.
      ruleMode,
      moveHistory,
      halfMoveClock,
      positionHistory: positionHistory ?? [],
      kingMoveStreak,
      frisianEndgamePly,
    };
    const match = findMatchingMove(getAllLegalMoves(board), premove);
    // Clear first so re-renders during the capture animation (pieces changing,
    // premove still set on the multi-capture step branch) don't re-trigger us.
    clearPremove();
    if (match) {
      log.info('GameBoard', 'Pre-move auto-play (matched)', { from: match.from, to: match.to, online: isOnlineMode });
      /*
       * QUI NON SI MARCA IL GESTO, ed e' voluto: il ref porta ancora quello con cui la
       * premossa e' stata inserita — `'tap'` dal pointerdown sulla destinazione, `'drag'`
       * dal drop — e la mossa dell'avversario nel frattempo non lo sovrascrive (passa
       * `'auto'` come argomento). La premossa e' una mossa NOSTRA che suona differita, non
       * una mossa arrivata da fuori: marcarla `'auto'` la farebbe suonare come una mossa
       * dell'avversario.
       */
      // Si smista come un clic sulla casella d'arrivo, con la lista ridotta alla mossa
      // trovata: mossa semplice o salto singolo -> `'simple'` (online solo spedizione,
      // niente applicazione locale, come il clic a mano); presa multipla ->
      // `'capture-direct'` -> `autoPlayCapture`, salto per salto.
      executeIntent(match.to, [match]);
    }
  }, [isOnlineMode, allowPremove, isMyTurn, onlinePlayerColor, premove, status, currentTurn, localPlayerColor, isReviewing, pieces, variant, ruleMode, moveHistory, halfMoveClock, positionHistory, kingMoveStreak, frisianEndgamePly, captureReplay, executeIntent, clearPremove]);

  const handlePointerMove = useCallback((event: React.PointerEvent<SVGSVGElement>) => {
    if (!dragState) return;
    // Solo il dito che ha preso il pezzo lo muove. Senza, su mobile un secondo dito
    // trascinava la pedina in mano al primo — la scacchiera risolve i tocchi per
    // COORDINATE, quindi niente le diceva da sola che quel puntatore non era lo stesso.
    if (dragState.pointerId !== null && event.pointerId !== dragState.pointerId) return;

    /*
     * SI SCRIVE IL `transform` QUI, SUBITO, senza rAF e senza toccare lo store.
     *
     * Prima le coordinate finivano in un ref e un rAF le versava nello store. Costava due
     * frame di ritardo sul dito — uno per il rinvio al frame, uno perché React schedula il
     * commit dopo il paint — e ogni `set` faceva girare i selettori di tutte le
     * sottoscrizioni a `useGameStore` (un centinaio nell'app) per un valore che leggeva
     * solo `DraggedPiece`.
     *
     * Scrivere un `transform` non invalida il layout: non c'è niente da coalescere, e il
     * compositore prende comunque l'ultimo valore prima del frame. Se il puntatore emette
     * più eventi di quanti frame ci siano, si sovrascrive una stringa — che costa molto
     * meno di quello che si è tolto.
     *
     * Lo scostamento è dall'ORIGINE CONGELATA (`offsetX/offsetY`, il punto della presa),
     * in unità SVG: il `<g>` vive dentro lo stesso `viewBox` della scacchiera.
     */
    const layer = dragLayerRef.current;
    if (!layer) return;
    const { x, y } = clientToSVG(event.clientX, event.clientY);
    /*
     * Lo scostamento si converte in PIXEL CSS: e' un `transform` CSS su un `<svg>`, non un
     * attributo SVG, ed e' quello che il compositore sa applicare senza ridipingere.
     * Il fattore e' lo stesso che usa `clientToSVG` al contrario.
     */
    const rect = boardRectRef.current;
    const k = rect && rect.width > 0 ? rect.width / boardPixelSize : 1;
    const dx = (x - dragState.offsetX) * k;
    const dy = (y - dragState.offsetY) * k;
    /*
     * A scacchiera girata la rotazione va scritta QUI e non lasciata alla classe: una
     * `style.transform` sostituisce quella del foglio, non la compone. L'ordine conta —
     * `rotate` prima, `translate` dopo — perche' cosi' la traslazione avviene nel frame
     * ruotato, che e' quello in cui `dx`/`dy` sono stati calcolati.
     */
    layer.style.transform = flipped
      ? `rotate(180deg) translate(${dx}px, ${dy}px)`
      : `translate(${dx}px, ${dy}px)`;
  }, [dragState, clientToSVG, boardPixelSize, flipped]);

  const handlePointerUp = useCallback((event: React.PointerEvent<SVGSVGElement>) => {
    if (!dragState) return;
    /*
     * E SOLO LUI LA RILASCIA. Senza questa riga, il `pointerup` del SECONDO dito
     * committava la mossa del primo — alle coordinate del secondo, cioè su una casella
     * che il giocatore non aveva mai toccato con quella pedina.
     */
    if (dragState.pointerId !== null && event.pointerId !== dragState.pointerId) return;

    // A commit from pointer-up is a drag-and-drop; stamp it for the move sound.
    setMoveSource('drag');
    resetDragLayer();
    // `hasPointerCapture` prima di rilasciare: su un puntatore mai catturato
    // `releasePointerCapture` lancia NotFoundError, e lo lancerebbe DENTRO un handler
    // React. Puo' capitare quando la presa non nasce da un pointerdown (i test) o quando
    // il browser ha gia' revocato la cattura da sé.
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    const { x, y } = clientToSVG(event.clientX, event.clientY);
    const targetSquare = svgCoordsToSquare(x, y, SQUARE_SIZE, boardSize, darkSquareBottomRight, allSquares);
    log.info('GameBoard', 'pointerUp coord→square', { targetSquare, flipped, boardSize });

    /*
     * Pre-mossa per trascinamento (turno avversario): la sorgente e' `dragState.square`.
     * La destinazione e' LIBERA, quindi non c'e' niente da filtrare: qualunque casella
     * accoda `(from, to)` come intento, e la mossa vera si risolve al cambio di turno.
     * Prima si distingueva «arrivo raggiungibile» da «pedina nostra -> annulla»; ora
     * l'elenco dei suggerimenti non contiene mai una casella occupata, quindi quel filtro
     * avrebbe respinto proprio la ricattura anticipata. Stessa regola del tocco
     * (`handlePremovePointerDown`): i due gesti dicono la stessa cosa.
     *
     * Lasciare la pedina dov'era (o fuori griglia) tiene la selezione, cosi' il tocco-tocco
     * continua a funzionare.
     */
    if (!isMyTurn) {
      if (targetSquare === null || targetSquare === dragState.square) {
        stopDrag();
      } else {
        log.info('GameBoard', 'Pre-mossa accodata per trascinamento', { from: dragState.square, to: targetSquare });
        setPremove({ from: dragState.square, to: targetSquare });
      }
      return;
    }

    // If dropped on same square, just select it (click behavior)
    // startDrag already set selectedSquare and legalMoves, just stop the drag
    if (targetSquare === dragState.square) {
      stopDrag();
      return;
    }

    // Dragging a piece that is mid-capture: dropping on the next hop advances the
    // sequence step by step; dropping on the final ring (unique line) auto-plays the
    // remaining hops, mirroring the tap behaviour. An imprecise drop snaps to the
    // nearest highlighted hop/final so the sequence isn't stalled by a near-miss.
    if (captureInProgress) {
      let stepSquare = targetSquare;
      let stepIntent = stepSquare !== null
        ? resolveCaptureStep(captureInProgress.possibleMoves, captureInProgress.currentStep, stepSquare)
        : { kind: 'none' as const };
      if (stepIntent.kind === 'none') {
        const snapped = resolveCaptureDropSquare(x, y, captureInProgress.possibleMoves, captureInProgress.currentStep, {
          squareSize: SQUARE_SIZE, boardSize, darkSquareBottomRight, allSquares, snapRadius: SNAP_RADIUS,
        });
        if (snapped !== null) {
          stepSquare = snapped;
          stepIntent = resolveCaptureStep(captureInProgress.possibleMoves, captureInProgress.currentStep, snapped);
        }
      }
      if (stepIntent.kind === 'finish') {
        stopDrag();
        autoPlaySteps(stepIntent.remaining);
      } else {
        log.info('GameBoard', 'Drop advances capture step', { targetSquare, stepSquare });
        // Un rilascio sull'ultimo salto CHIUDE la presa: `endDrag` restituisce la
        // mossa, e la spedizione parte da lì come per gli altri percorsi.
        inviaSeCompletata(endDrag(stepSquare));
      }
      return;
    }

    // Drop on a valid destination: simple move, whole-capture (final square), or
    // start a step-by-step capture (first hop).
    if (targetSquare !== null && executeIntent(targetSquare)) {
      log.info('GameBoard', 'Drop executed move', { targetSquare, flipped });
      stopDrag();
      return;
    }

    // Imprecise drop: snap to the nearest legal destination for the dragged piece so
    // the player needn't land exactly on the target cell. A forced (single) move
    // completes on any on-board drop; with several options we snap only within
    // SNAP_RADIUS of one, otherwise fall through to cancel.
    const snapped = resolveDropSquare(x, y, legalMoves, {
      squareSize: SQUARE_SIZE, boardSize, darkSquareBottomRight, allSquares, snapRadius: SNAP_RADIUS,
    });
    if (snapped !== null && executeIntent(snapped)) {
      log.info('GameBoard', 'Drop snapped to nearest legal destination', { targetSquare, snapped });
      stopDrag();
      return;
    }

    // Invalid drop: cancel the drag and deselect.
    log.warn('GameBoard', 'Invalid drop, cancelling drag', { targetSquare });
    cancelDrag();
  }, [dragState, clientToSVG, resetDragLayer, stopDrag, endDrag, cancelDrag, executeIntent, autoPlaySteps, captureInProgress, isMyTurn, pieces, currentTurn, legalMoves, setPremove, boardSize, darkSquareBottomRight, allSquares]);

  const handlePointerCancel = useCallback((event: React.PointerEvent<SVGSVGElement>) => {
    // Come per move e up: solo il dito che ha preso il pezzo può annullarne la presa.
    if (dragState && dragState.pointerId !== null && event.pointerId !== dragState.pointerId) return;
    log.info('GameBoard', 'Pointer cancel, cancelling drag');
    resetDragLayer();
    cancelDrag();
  }, [dragState, resetDragLayer, cancelDrag]);

  /*
   * IL BROWSER DICE CHE QUEL DITO NON C'E' PIU'.
   *
   * `lostpointercapture` arriva anche dove un `pointerup` non arriva, ed e' l'unico
   * modo che abbiamo di sapere che la cattura se n'e' andata da se'. Sul percorso
   * felice e' un colpo a vuoto: e' dispatchato DOPO il `pointerup`, e a quel punto
   * `stopDrag`/`cancelDrag`/`endDrag`/`setPremove` hanno gia' azzerato `dragState`.
   * La guardia `!dragState` non e' prudenza: senza, si porterebbe via la selezione del
   * tap-tap a ogni rilascio.
   */
  const handleLostPointerCapture = useCallback((event: React.PointerEvent<SVGSVGElement>) => {
    if (!dragState || dragState.pointerId !== event.pointerId) return;
    log.info('GameBoard', 'cattura del puntatore persa, presa sciolta', { pointerId: event.pointerId });
    resetDragLayer();
    cancelDrag();
  }, [dragState, resetDragLayer, cancelDrag]);

  /*
   * UNA PRESA NON ATTRAVERSA UN CAMBIO DI APP.
   *
   * Schermo che si spegne, notifica, app mandata in secondo piano col dito giu': il
   * `pointerup` puo' non arrivare mai, e su una pagina congelata puo' non arrivare
   * nemmeno il `pointercancel` — cioe' l'unico caso che `hasPointerCapture` non copre,
   * perche' la cattura resterebbe registrata su un dito morto. Qui la presa si chiude
   * comunque, ed e' anche il comportamento giusto nel merito: al rientro la pedina e'
   * posata, non ancora in mano.
   *
   * L'ascolto si arma solo mentre una presa c'e'.
   */
  useEffect(() => {
    if (!dragState) return;
    const sciogli = () => {
      log.info('GameBoard', 'app in secondo piano, presa sciolta');
      resetDragLayer();
      cancelDrag();
    };
    const suVisibilita = () => { if (document.hidden) sciogli(); };
    document.addEventListener('visibilitychange', suVisibilita);
    window.addEventListener('pagehide', sciogli);
    return () => {
      document.removeEventListener('visibilitychange', suVisibilita);
      window.removeEventListener('pagehide', sciogli);
    };
  }, [dragState, resetDragLayer, cancelDrag]);

  // Calculate which squares to highlight. Rings (.capture-target) mark a landing where
  // clicking COMPLETES a capture; dots (.move-hint) mark a quiet move or an intermediate
  // hop of a multi-capture. This stays consistent before AND during a multi-capture.
  let legalDestinations: Set<number>;
  let captureTargets: Set<number>;
  if (captureInProgress && captureInProgress.possibleMoves.length > 0) {
    // Mid-sequence: dots on the next hop(s); the ring stays on the FINAL landing(s) of
    // the still-possible lines for the WHOLE capture (so it's visible during the middle
    // hops of a 3+ jump too, not only at the first and last). A final shared by several
    // lines is ambiguous → no ring (matches the initial-selection behaviour).
    const { possibleMoves, currentStep } = captureInProgress;
    legalDestinations = new Set<number>();
    const finalCounts = new Map<number, number>();
    for (const move of possibleMoves) {
      if (!move.path || currentStep >= move.path.length) continue;
      const step = move.path[currentStep];
      if (step !== undefined) legalDestinations.add(step);
      finalCounts.set(move.to, (finalCounts.get(move.to) ?? 0) + 1);
    }
    captureTargets = new Set<number>();
    for (const [to, count] of finalCounts) {
      if (count === 1) captureTargets.add(to);
    }
  } else if (!isMyTurn) {
    /*
     * Si sta componendo una PRE-MOSSA, e una pre-mossa e' un `(from, to)`: il pallino va
     * sull'ARRIVO di ogni mossa, mai su un salto intermedio. Il ramo qui sotto lo
     * metterebbe su `path[0]`, e toccarlo accoderebbe un `to` che al cambio di turno non
     * combacia con nessuna mossa legale: la pre-mossa sparirebbe in silenzio. Un pallino
     * che tradisce.
     *
     * Che qui `legalMoves` contenga solo mosse da pre-mossa lo garantisce `!isMyTurn`:
     * mentre vale, `handlePointerDown` dirotta ogni tocco al ramo pre-mossa, quindi
     * `selectSquare`/`startDrag` non possono scriverci.
     */
    legalDestinations = new Set(legalMoves.map((m) => m.to));
    captureTargets = premoveCaptureLandings(legalMoves);
  } else {
    // Initial selection: dot on first hop (multi-capture) or destination; ring on the
    // landing of any capture that uniquely identifies one line.
    legalDestinations = new Set(legalMoves.map((m) => {
      if (m.path && m.path.length > 1 && m.path[0] !== undefined) {
        return m.path[0]; // First intermediate step
      }
      return m.to;
    }));
    captureTargets = captureTargetSquares(legalMoves);
  }
  // A capture landing is shown as a ring, not a dot — drop any overlap so single
  // captures (and unambiguous finals) render only the ring.
  captureTargets.forEach((sq) => legalDestinations.delete(sq));

  // Opponent pieces jumped so far in the in-progress multi-capture: keep them on the
  // board but faded, until they're all removed at the final landing.
  // Stessa cosa per il replay della presa altrui: li' le pedine ancora da saltare sono
  // vive nel fotogramma congelato, anche se la damiera vera le ha gia' tolte tutte.
  const capturedSquares = captureInProgress
    ? new Set(captureInProgress.capturedSoFar)
    : replay
      ? new Set(replay.move.captures.slice(0, replay.step).map((c) => c.square))
      : new Set<number>();

  // Pezzi da illuminare dopo un tocco a vuoto. Il nonce viaggia fino al pezzo, che lo usa
  // per far ripartire il lampo se lo si ritocca (vedi Piece.css); qui si decide solo CHI.
  const hintSquares = showMoveHints && movableHintEnabled && movableHint
    ? new Set(movableHint.squares)
    : null;
  const hintNonce = movableHint?.nonce ?? 0;

  /**
   * IL FONDO DELLA SCACCHIERA, memoizzato: le cento caselle e la lastra del materiale.
   *
   * Non dipendono da niente che cambi quando si gioca — solo dal lato, dalla convenzione
   * di parità e dal materiale — ma venivano ricostruite a ogni render di `GameBoard`, e
   * `GameBoard` si ri-renderizza a ogni selezione, a ogni mossa e a ogni aiuto acceso.
   * Cento `<rect>` nuovi ogni volta sono cento elementi da creare e da confrontare.
   *
   * Con `useMemo` l'array è lo STESSO oggetto fra un render e l'altro, e React salta del
   * tutto quel sottoalbero invece di attraversarlo. Misurato sul profilo di un telefono
   * del 2017: il gesto «tocco la pedina e compaiono i pallini» spendeva 2397 ms di
   * JavaScript su tre secondi di prese e rilasci ripetuti.
   */
  const caselle = useMemo(
    () => Array.from({ length: boardSize }, (_, row) =>
      Array.from({ length: boardSize }, (_, col) => {
        const isDark = isPlayableSquare(row + 1, col + 1, boardSize, darkSquareBottomRight);
        return (
          <rect
            key={`${row}-${col}`}
            x={col * SQUARE_SIZE}
            y={row * SQUARE_SIZE}
            width={SQUARE_SIZE}
            height={SQUARE_SIZE}
            className={isDark ? 'square-dark' : 'square-light'}
          />
        );
      })
    ),
    [boardSize, darkSquareBottomRight],
  );

  const lastra = useMemo(
    () => (
      <BoardSlab
        size={boardPixelSize}
        squareSize={SQUARE_SIZE}
        darkSquareBottomRight={darkSquareBottomRight}
        material={boardMaterial}
      />
    ),
    [boardPixelSize, darkSquareBottomRight, boardMaterial],
  );

  // Find the piece being moved during capture sequence
  const movingPiece = captureInProgress
    ? pieces.find((p) => p.square === captureInProgress.originalFrom)
    : null;

  // Lo stesso, per la presa altrui: il pezzo va cercato nel FOTOGRAMMA, non nella damiera
  // viva, che lo ha gia' portato in fondo (o promosso).
  const replayPiece = replay ? replay.pieces.find((p) => p.square === replay.move.from) : null;

  /**
   * I PALLINI DELLA DAMA BLOCCATA (Frisone / Frysk!).
   *
   * Nelle due varianti frisoni una dama non puo' fare piu' di `blockedKingMaxMoves` mosse
   * di fila senza catturare: un pallino per ogni mossa gia' fatta, tre e la dama e' ferma.
   *
   * LE CONDIZIONI LE DICE IL MOTORE (`getBlockedKingState`), non questa funzione — gate di
   * variante, esenzione di chi non ha piu' pedine (art. 17: una dama sola non e' vincolata,
   * e i suoi pallini sono spenti), presenza della dama sulla casella. Una copia qui
   * dentro driftierebbe dalla regola vera, che e' esattamente cio' che e' successo a
   * `minimax.ts` con `determineGameStatus`. Si chiede per i DUE colori: anche la dama
   * avversaria conta le sue mosse, e saperlo cambia come si gioca.
   *
   * La striscia segue i pezzi DISEGNATI, non la damiera viva: in revisione `reviewPieces`
   * guarda un ply passato, e la striscia viva pianterebbe i pallini su una casella dove
   * quella dama non c'e' ancora o non c'e' piu'. Stessa guardia dei pezzi: senza la
   * ricostruzione si ricade sul vivo insieme a loro.
   */
  const blockedKingDots = useMemo(() => {
    // ALLA CIECA SI SPEGNE, come ogni altro aiuto: un pallino direbbe dove sta una dama,
    // che e' precisamente cio' che la modalita' nasconde. Durante una sbirciata torna.
    if (blindActive) return [];
    const streak = isReviewing && reviewPieces ? reviewKingMoveStreak : kingMoveStreak;
    if (!streak) return [];
    const out: { square: Square; count: number; blocked: boolean; core: string; rim: string; blockingRim: string }[] = [];
    for (const color of [Color.WHITE, Color.BLACK] as const) {
      const state = getBlockedKingState(renderPieces, variant, color, streak);
      if (!state) continue;
      /*
       * Il pezzo non e' disegnato dov'e' la sua casella: e' in mano, o sta percorrendo una
       * presa — la propria o quella che sta subendo. I pallini stanno al PIEDE DI UN PEZZO,
       * e appesi a una casella vuota sarebbero un errore. (Una presa azzera comunque la
       * striscia appena finisce: qui si copre solo l'animazione.)
       */
      if (dragState?.square === state.square) continue;
      if (captureInProgress?.originalFrom === state.square) continue;
      if (replay?.move.from === state.square) continue;
      out.push({
        square: state.square,
        // Il motore non cappa: dice la striscia come l'ha contata. Con la regola in vigore
        // non puo' superare il tetto (al tetto la dama e' ferma, e qualunque cosa la
        // sblocchi azzera la striscia), ma il cap resta qui perche' un quarto pallino
        // racconterebbe una regola che non esiste.
        count: Math.min(state.count, state.limit),
        blocked: state.blocked,
        ...blockedKingDotInk(pieceColorPair, pieceStyle, color === Color.WHITE),
      });
    }
    return out;
  }, [
    blindActive, isReviewing, reviewPieces, reviewKingMoveStreak, kingMoveStreak,
    renderPieces, variant, dragState, captureInProgress, replay, pieceColorPair, pieceStyle,
  ]);

  return (
    <PieceLodContext.Provider value={pieceLod}>
    <div className="game-board-container">
      <svg
        ref={svgRef}
        className={`game-board ${flipped ? 'flipped' : ''}${blind ? ' blind' : ''}${blind && peeking ? ' peeking' : ''}`}
        viewBox={`0 0 ${boardPixelSize} ${boardPixelSize}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onLostPointerCapture={handleLostPointerCapture}
        style={{
          touchAction: 'none',
          '--color-square-light': boardColors.light,
          '--color-square-dark': boardColors.dark,
        } as CSSProperties}
      >
        {/* Il pallino delle mosse non e' un disco pieno: sfuma sul proprio bordo esterno, cosi'
            sta sulla casella invece di sembrarci appiccicato sopra. Serve un gradiente per
            PARITA' e non uno solo perche' il colore e' derivato dalla casella, e sulle varianti
            a caselle tutte giocabili le due parita' possono avere due verdi diversi.

            I due `<stop>` iniziali hanno lo stesso colore e opacita' piena: e' il NUCLEO, il
            disco vero, largo `DOT_CORE_FRACTION` del raggio. La garanzia di leggibilita' e'
            misurata li' — vedi `board-highlight-colors.ts` — quindi la sfumatura vive tutta
            fuori dal nucleo e non lo intacca. */}
        <defs>
          {([true, false] as const).map((dark) => (
            <radialGradient key={`hint-grad-${dark}`} id={hintGradientId(dark)}>
              <stop offset="0" stopColor={dark ? hintColors.dark : hintColors.light} stopOpacity="1" />
              <stop offset={DOT_CORE_FRACTION} stopColor={dark ? hintColors.dark : hintColors.light} stopOpacity="1" />
              <stop offset="1" stopColor={dark ? hintColors.dark : hintColors.light} stopOpacity="0" />
            </radialGradient>
          ))}
        </defs>

        {/* Render the board squares */}
        {caselle}

        {/* Venatura del materiale: una lastra sola su tutta la scacchiera, sopra i
            colori di casella e sotto ogni evidenziazione. */}
        {lastra}

        {/* Highlight last move squares (from, to, captures) */}
        {showLastMove && lastMoveSquares.from !== null && (() => {
          const { x, y } = squareToSVGCoords(lastMoveSquares.from, SQUARE_SIZE, boardSize, darkSquareBottomRight, allSquares);
          return (
            <rect
              key={`last-move-from-${lastMoveSquares.from}`}
              x={x - SQUARE_SIZE / 2}
              y={y - SQUARE_SIZE / 2}
              width={SQUARE_SIZE}
              height={SQUARE_SIZE}
              className="square-last-move"
              pointerEvents="none"
            />
          );
        })()}
        {showLastMove && lastMoveSquares.to !== null && (() => {
          const { x, y } = squareToSVGCoords(lastMoveSquares.to, SQUARE_SIZE, boardSize, darkSquareBottomRight, allSquares);
          return (
            <rect
              key={`last-move-to-${lastMoveSquares.to}`}
              x={x - SQUARE_SIZE / 2}
              y={y - SQUARE_SIZE / 2}
              width={SQUARE_SIZE}
              height={SQUARE_SIZE}
              className="square-last-move"
              pointerEvents="none"
            />
          );
        })()}
        {/* Highlight a queued pre-move while it's the opponent's turn: both squares, since a
            pre-move is always piece → square. */}
        {premove && !isReviewing && !blindActive && (
          [{ sq: premove.from, role: 'from' }, { sq: premove.to, role: 'to' }]
        ).map(({ sq, role }) => {
          const { x, y } = squareToSVGCoords(sq, SQUARE_SIZE, boardSize, darkSquareBottomRight, allSquares);
          return (
            <rect
              key={`premove-${role}-${sq}`}
              x={x - SQUARE_SIZE / 2}
              y={y - SQUARE_SIZE / 2}
              width={SQUARE_SIZE}
              height={SQUARE_SIZE}
              className="square-premove"
              pointerEvents="none"
            />
          );
        })}
        {/* Candidate pieces for an ambiguous capture: tap one to capture with it */}
        {!blindActive && captureSourceChoices.map((sq) => {
          const { x, y } = squareToSVGCoords(sq, SQUARE_SIZE, boardSize, darkSquareBottomRight, allSquares);
          return (
            <rect
              key={`capture-source-${sq}`}
              x={x - SQUARE_SIZE / 2}
              y={y - SQUARE_SIZE / 2}
              width={SQUARE_SIZE}
              height={SQUARE_SIZE}
              className="square-capture-source"
              pointerEvents="none"
              style={{ '--hl-dot': hintColorFor(sq) } as CSSProperties}
            />
          );
        })}
        {hintSquare !== null && !blindActive && (() => {
          const { x, y } = squareToSVGCoords(hintSquare, SQUARE_SIZE, boardSize, darkSquareBottomRight, allSquares);
          return (
            <rect
              key={`hint-square-${hintSquare}`}
              x={x - SQUARE_SIZE / 2}
              y={y - SQUARE_SIZE / 2}
              width={SQUARE_SIZE}
              height={SQUARE_SIZE}
              className="square-hint"
              pointerEvents="none"
              style={{ '--hl-dot': hintColorFor(hintSquare) } as CSSProperties}
            />
          );
        })()}
        {showLastMoveCaptures && lastMoveSquares.captures.map((sq) => {
          const { x, y } = squareToSVGCoords(sq, SQUARE_SIZE, boardSize, darkSquareBottomRight, allSquares);
          return (
            <rect
              key={`last-move-capture-${sq}`}
              x={x - SQUARE_SIZE / 2}
              y={y - SQUARE_SIZE / 2}
              width={SQUARE_SIZE}
              height={SQUARE_SIZE}
              className="square-last-move-capture"
              pointerEvents="none"
            />
          );
        })}

        {/* Highlight legal move destinations */}
        {showMoveHints && Array.from(legalDestinations).map((square) => {
          const { x, y } = squareToSVGCoords(square, SQUARE_SIZE, boardSize, darkSquareBottomRight, allSquares);
          return (
            <circle
              key={`hint-${square}`}
              cx={x}
              cy={y}
              // L'ingombro che il pallino ha avuto per tutta la sua storia. Per un attimo era
              // stato allargato a 0.24 perche' la sfumatura si aggiungesse FUORI lasciando
              // intatto il nucleo: leggibile sulla carta, troppo grande a schermo. Ora la
              // dissolvenza vive dentro questo raggio (vedi `DOT_CORE_FRACTION`).
              r={SQUARE_SIZE * 0.15}
              className="move-hint"
              // `fill` come STILE e non come attributo: un attributo di presentazione perde
              // contro la regola CSS, e il pallino resterebbe piatto senza che nulla lo segnali.
              style={{
                '--hl-dot': hintColorFor(square),
                fill: `url(#${hintGradientId(isHintSquareDark(square))})`,
              } as CSSProperties}
            />
          );
        })}

        {/* Ring on the final square of a multi-capture: play the whole capture at once */}
        {showMoveHints && Array.from(captureTargets).map((square) => {
          const { x, y } = squareToSVGCoords(square, SQUARE_SIZE, boardSize, darkSquareBottomRight, allSquares);
          return (
            <circle
              key={`capture-target-${square}`}
              cx={x}
              cy={y}
              r={SQUARE_SIZE * 0.40}
              className="capture-target"
              pointerEvents="none"
              style={{ '--hl-dot': hintColorFor(square) } as CSSProperties}
            />
          );
        })}

        {/* Etichette delle caselle (preferenza "Notazione" in Grafica; 'none' di default,
            e allora questo non disegna nulla). `rotate`: qui il ribaltamento è la
            rotazione CSS dell'intero <svg>, quindi ogni etichetta si contro-ruota come
            fa ogni pezzo. Qui e non in fondo: dopo ogni evidenziazione di casella — così
            i numeri restano nitidi sopra gli aloni e sopra l'anello di presa, che
            attraversa proprio l'angolo dell'etichetta — ma PRIMA dei pezzi, che devono
            passarci sopra. */}
        <BoardCoords
          notation={boardNotation}
          boardSize={boardSize}
          squareSize={SQUARE_SIZE}
          darkSquareBottomRight={darkSquareBottomRight}
          allSquares={allSquares}
          flipped={flipped}
          flipMode="rotate"
        />

        {/* Render pieces (non-dragged). Captured pieces stay visible but faded. */}
        {renderPieces.map((piece) => {
          // Hide dragged piece
          const isDragging = dragState?.square === piece.square;
          if (isDragging) return null;

          // Hide the moving piece during capture (we render it at intermediate position)
          if (captureInProgress && piece.square === captureInProgress.originalFrom) {
            return null;
          }

          // Stessa ragione, per la presa altrui che sta scorrendo.
          if (replay && piece.square === replay.move.from) {
            return null;
          }

          const { x, y } = squareToSVGCoords(piece.square, SQUARE_SIZE, boardSize, darkSquareBottomRight, allSquares);
          const isSelected = selectedSquare === piece.square;

          return (
            <Piece
              key={piece.square}
              piece={piece}
              x={x}
              y={y}
              size={SQUARE_SIZE * 0.8}
              isSelected={isSelected}
              movableHint={hintSquares?.has(piece.square) ? hintNonce : null}
              // Already jumped this sequence: fade it until the final landing removes it.
              captured={capturedSquares.has(piece.square)}
              flipped={flipped}
              colorPair={pieceColorPair}
              // Sul vetro ogni pezzo si specchia: e' cio' che dice che la lastra e' vetro.
              reflection={boardMaterial === 'glass'}
              // Il vetro specchia di piu' dove lo sguardo e' radente, cioe' verso i bordi.
              reflectionIncidence={glassIncidence(x, y, boardPixelSize, SQUARE_SIZE)}
            />
          );
        })}

        {/* Render piece at intermediate position during multi-capture (only when not dragging) */}
        {captureInProgress && movingPiece && !dragState && (() => {
          const { x, y } = squareToSVGCoords(captureInProgress.currentSquare, SQUARE_SIZE, boardSize, darkSquareBottomRight, allSquares);
          return (
            <Piece
              key={`capturing-${movingPiece.square}`}
              piece={movingPiece}
              x={x}
              y={y}
              size={SQUARE_SIZE * 0.8}
              isSelected={true}
              // Il confronto è sulla casella CORRENTE, non su piece.square: questo <Piece> è
              // il pezzo partito da originalFrom, disegnato dove la presa è arrivata.
              movableHint={hintSquares?.has(captureInProgress.currentSquare) ? hintNonce : null}
              ghost
              flipped={flipped}
              colorPair={pieceColorPair}
              reflection={boardMaterial === 'glass'}
              reflectionIncidence={glassIncidence(x, y, boardPixelSize, SQUARE_SIZE)}
            />
          );
        })()}

        {/* Il pezzo dell'AVVERSARIO a meta' della sua presa multipla. Come quello sopra,
            ma senza `isSelected`: la selezione e' un'affordance, dice «questo pezzo e'
            tuo, tocca dove vuoi mandarlo» — su una mossa che sta subendo sarebbe una
            bugia. Il `ghost` invece resta: e' il segno che il pezzo e' in viaggio. */}
        {replay && replayPiece && (() => {
          const step = replay.move.path?.[replay.step - 1];
          if (step === undefined) return null;
          const { x, y } = squareToSVGCoords(step, SQUARE_SIZE, boardSize, darkSquareBottomRight, allSquares);
          return (
            <Piece
              key={`replaying-${replay.move.from}`}
              piece={replayPiece}
              x={x}
              y={y}
              size={SQUARE_SIZE * 0.8}
              isSelected={false}
              movableHint={null}
              ghost
              flipped={flipped}
              colorPair={pieceColorPair}
              reflection={boardMaterial === 'glass'}
              reflectionIncidence={glassIncidence(x, y, boardPixelSize, SQUARE_SIZE)}
            />
          );
        })()}

        {/* I PALLINI DELLA DAMA BLOCCATA: ultimi dentro l'<svg>, quindi SOPRA i pezzi.
            Sopra e non sotto perche' il segno sormonta il piede della dama: fra il fondo
            della sagoma e il bordo della casella corrono ~6 unita' su 90, cioe' 2.4px su
            una damiera da 360, e un pallino la' dentro non si vedrebbe (vedi
            blocked-king-dots.ts). Disegnati per ultimi passano anche sopra l'ombra di
            contatto e sopra il riflesso del vetro, che scendono entrambi sotto il pezzo.

            `rotate` come su ogni contenuto per-pezzo: a damiera girata e' la rotazione CSS
            dell'intero <svg> a girare tutto, quindi senza contro-rotazione questa fila
            finirebbe sopra la TESTA della dama invece che al suo piede. */}
        {blockedKingDots.map(({ square, count, blocked, core, rim, blockingRim }) => {
          const { x, y } = squareToSVGCoords(square, SQUARE_SIZE, boardSize, darkSquareBottomRight, allSquares);
          return (
            <g
              key={`blocked-king-${square}`}
              className="blocked-king-dots"
              // Su questi asseriscono i test, invece di contare nodi SVG: contarli lega il
              // test alla struttura del disegno (la lezione di Piece.relief.test.tsx).
              data-square={square}
              data-count={count}
              aria-hidden="true"
              pointerEvents="none"
              transform={flipped ? `rotate(180 ${x} ${y})` : undefined}
              style={{ '--bk-core': core, '--bk-rim': rim, '--bk-blocking-rim': blockingRim } as CSSProperties}
            >
              {Array.from({ length: count }, (_, i) => (
                <circle
                  key={i}
                  // Fila CENTRATA, non riempita da sinistra: su una damiera che si gira una
                  // fila asimmetrica sembrerebbe un difetto di orientamento.
                  cx={x + (i - (count - 1) / 2) * SQUARE_SIZE * BK_DOT_STEP}
                  cy={y + SQUARE_SIZE * BK_DOT_CY}
                  r={SQUARE_SIZE * BK_DOT_R}
                  // L'ultimo pallino porta l'ambra solo quando la dama e' DAVVERO ferma.
                  className={blocked && i === count - 1 ? 'is-blocking' : undefined}
                />
              ))}
            </g>
          );
        })}

      </svg>

      {/* La pedina trascinata sta FUORI dall'<svg> della scacchiera, in un <svg> suo.
          Non e' un vezzo di struttura: in Blink i figli di un SVG non possono avere un
          layer proprio, un <svg> radice si'. Vedi la nota in `DraggedPiece`. */}
      <DraggedPiece
        ref={dragLayerRef}
        squareSize={SQUARE_SIZE}
        boardPixelSize={boardPixelSize}
        flipped={flipped}
        colorPair={pieceColorPair}
      />
    </div>
    </PieceLodContext.Provider>
  );
}

// Memoized so parent re-renders (e.g. the 1s game:clock-sync updating the
// clock display, or any GameScreen state change) don't re-render the whole
// board mid-drag. GameBoard still reacts to game state via its store
// subscriptions; memo only gates prop-driven re-renders from the parent.
export default memo(GameBoard);
